import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth-store'
import { useTenantStore } from '@/stores/tenant-store'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  children: React.ReactNode
}

/**
 * ProtectedRoute - Wrapper for routes that require authentication
 * Per Frontend-Architecture.md Section 5 and Common-Screens.md Section 1.4:
 * - Redirects unauthenticated users to /login
 * - Preserves intended destination for post-login redirect
 * - Shows loading state during initial auth check
 * - No flash of wrong content
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation()
  const { isAuthenticated, isInitialized } = useAuthStore()

  // Tenant-profile hydration for already-authenticated sessions (page
  // refresh / returning tab): the login flow hydrates on fresh logins; this
  // covers sessions restored from persisted auth state. Fire-and-forget.
  useEffect(() => {
    if (!isAuthenticated) return
    const { isHydrated, hydrate } = useTenantStore.getState()
    const token = useAuthStore.getState().tokens?.accessToken
    if (!isHydrated && token) void hydrate(token)
  }, [isAuthenticated])

  // Show loading spinner while checking auth state
  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
